/**
 * Validation for the Onboarding Wizard (Week 4, client-side only).
 *
 * `validateStep(index, config)` returns the FIRST error string for the
 * given step, or `null` when the step is valid. The wizard uses this to
 * gate forward navigation (Next) per step.
 *
 * Cross-step logic lives in `isMetricsStepRequired` / `visibleStepCount`,
 * which decide whether the Metrics step should be shown or skipped based
 * on the config catalog (tool type + presence of metric candidates).
 *
 * Pure functions: no React, no I/O.
 */

import { OnboardingConfig } from './types';

// ─── Patterns ────────────────────────────────────────────────────────────

/** Tool name: letter first, then letters / digits / underscores. */
export const TOOL_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/** Secrets Manager ARN, e.g. arn:aws:secretsmanager:us-east-1:123456789012:secret:foo */
export const SECRET_ARN_PATTERN =
  /^arn:aws:secretsmanager:[a-z0-9-]+:\d{12}:secret:.+$/;

// ─── Step indices ──────────────────────────────────────────────────────
// Logical step identifiers. Note these are the *logical* steps; the
// rendered wizard may hide the Metrics step (see isMetricsStepRequired).

export const STEP_SCHEMA = 0;
export const STEP_METADATA = 1;
export const STEP_METRICS = 2;
export const STEP_PARAMETERS = 3;
export const STEP_DESCRIPTION = 4;
export const STEP_YAML_PREVIEW = 5;

// ─── Cross-step visibility ───────────────────────────────────────────────

/**
 * Whether the Metrics step should be shown.
 *
 * For v1 the only tool type is UNIVERSAL_STORED_PROCEDURE, which is driven
 * by base-metric definitions, so metrics are required. Written as a guard so
 * future tool types that don't use metric catalogs can skip the step without
 * touching the wizard component.
 */
export function isMetricsStepRequired(config: OnboardingConfig): boolean {
  return config.toolType === 'UNIVERSAL_STORED_PROCEDURE';
}

/**
 * Number of visible wizard steps given current config.
 *
 * The Review & Generate (YAML preview) step is always present, so the count is
 * one higher than the number of data-entry steps.
 */
export function visibleStepCount(config: OnboardingConfig): number {
  return isMetricsStepRequired(config) ? 6 : 5;
}

// ─── Per-step validation ─────────────────────────────────────────────────

function validateSchemaStep(config: OnboardingConfig): string | null {
  // The first step now also owns the database connection (cluster, database,
  // and the three secret ARNs were merged in from the old Database step), so
  // schema import AND a valid DB config are both required to advance.
  if (!config.sourceTableName.trim()) {
    return 'Import and parse a schema before continuing.';
  }
  const { clusterIdentifier, database, secretArns } = config.database;
  if (!clusterIdentifier.trim()) {
    return 'Cluster identifier is required.';
  }
  if (!database.trim()) {
    return 'Database is required.';
  }
  const envs: Array<{ key: keyof typeof secretArns; label: string }> = [
    { key: 'test', label: 'Test' },
    { key: 'beta', label: 'Beta' },
    { key: 'live', label: 'Live' },
  ];
  for (const { key, label } of envs) {
    const arn = secretArns[key];
    if (!arn.trim()) {
      return `${label} secret ARN is required.`;
    }
    if (!SECRET_ARN_PATTERN.test(arn)) {
      return `${label} secret ARN is not a valid Secrets Manager ARN.`;
    }
  }
  return null;
}

function validateMetadataStep(config: OnboardingConfig): string | null {
  if (!config.toolName.trim()) {
    return 'Tool name is required.';
  }
  if (!TOOL_NAME_PATTERN.test(config.toolName)) {
    return 'Tool name must start with a letter and contain only letters, numbers, and underscores.';
  }
  if (!config.category.trim()) {
    return 'Category is required.';
  }
  return null;
}

function validateMetricsStep(config: OnboardingConfig): string | null {
  if (config.baseMetrics.length === 0) {
    return 'Define at least one base metric.';
  }

  const seenKeys = new Set<string>();
  for (let i = 0; i < config.baseMetrics.length; i++) {
    const m = config.baseMetrics[i];
    const key = m.metricKey.trim();
    if (!key) {
      return `Base metric #${i + 1} is missing a metric key.`;
    }
    // Compare on the trimmed key so whitespace-only variants ("rev" vs "rev ")
    // are caught as duplicates rather than producing colliding keys downstream.
    if (seenKeys.has(key)) {
      return `Duplicate base metric key "${key}".`;
    }
    seenKeys.add(key);
    if (!m.exprSql.trim()) {
      return `Base metric "${key}" is missing a SQL expression.`;
    }
    // A version-axis reference no longer blocks Next: it is now surfaced as a
    // dismissible warning at the catalog write step (see findVersionAxisErrors /
    // CatalogMetadataSection) rather than gating navigation here.
  }

  // Derived metrics are optional, but any present row must be complete and
  // reference at least one base metric.
  for (let i = 0; i < config.derivedMetrics.length; i++) {
    const d = config.derivedMetrics[i];
    if (!d.derivedMetricKey.trim()) {
      return `Derived metric #${i + 1} is missing a metric key.`;
    }
    if (!d.baseMetricsCsv.trim()) {
      return `Derived metric "${d.derivedMetricKey}" must reference at least one base metric.`;
    }
    if (!d.exprSql.trim()) {
      return `Derived metric "${d.derivedMetricKey}" is missing a SQL expression.`;
    }
  }

  return null;
}

/**
 * Parameters step (input_schema). Deliberately PERMISSIVE — parameters are
 * proposed by the LLM and refined by the user, so the only hard requirements
 * are that every row has a non-empty name and every name listed as required
 * actually corresponds to a defined parameter.
 */
function validateParametersStep(config: OnboardingConfig): string | null {
  const seen = new Set<string>();
  for (let i = 0; i < config.parameters.length; i++) {
    const name = config.parameters[i].parameterName.trim();
    if (!name) {
      return `Parameter #${i + 1} is missing a name.`;
    }
    // Duplicate names would collide as keys in the YAML input_schema map.
    if (seen.has(name)) {
      return `Duplicate parameter name "${name}".`;
    }
    seen.add(name);
  }

  // `required` is the per-row boolean (single source of truth) — no separate
  // list to reconcile, so there's no way to require a name that isn't defined.
  return null;
}

/**
 * Description step (Spec 6). Deliberately PERMISSIVE — the description is
 * LLM-generated and freely editable, and is optional for advancing (it is
 * folded into the final YAML but never blocks Finish). Always valid.
 */
function validateDescriptionStep(_config: OnboardingConfig): string | null {
  return null;
}

/**
 * YAML preview step (Review & Generate). A read-only review of the assembled
 * YAML config — there is nothing to validate, so it is always valid.
 */
function validateYamlPreviewStep(_config: OnboardingConfig): string | null {
  return null;
}

/**
 * Validate a single wizard step. Returns the first error message, or `null`
 * if the step is valid.
 */
export function validateStep(index: number, config: OnboardingConfig): string | null {
  switch (index) {
    case STEP_SCHEMA:
      return validateSchemaStep(config);
    case STEP_METADATA:
      return validateMetadataStep(config);
    case STEP_METRICS:
      return validateMetricsStep(config);
    case STEP_PARAMETERS:
      return validateParametersStep(config);
    case STEP_DESCRIPTION:
      return validateDescriptionStep(config);
    case STEP_YAML_PREVIEW:
      return validateYamlPreviewStep(config);
    default:
      return null;
  }
}

/** Convenience: whether the given step currently passes validation. */
export function isStepValid(index: number, config: OnboardingConfig): boolean {
  return validateStep(index, config) === null;
}
